
import { Component, ChangeDetectionStrategy, ViewChild, ElementRef, Input, HostBinding } from '@angular/core';
import { WDSSortField, WizCollectionUtils } from '@wizeoo/akm-core-lib';
import { WizRoot, WizTextInput, WizEventManager, WizPopUp, WizIcon} from '@wizeoo/akm-component-lib';
import { DataPack, VisionKey, WidgetConfigurationItemObj } from '@wizeoo/akm-message-lib';
import { BaselineEngine, FeiKeyDef, FeiVO, FIELD_KIND, FreedataUpdatePack, LoopResult, WidgetLine } from '@wizeoo/akm-compute-lib';
import { AkmFDVCell, KeyPair, WizConstants } from '@wizeoo/akm-compute-lib';
import { cloneDeep } from 'lodash-es';

import { ALK_EVENT_TYPE, CHART_WIDGET_MODE, WIDGET_ACTION_ORIGIN, WIDGET_GLOBAL_KIND, WIDGET_KIND, WIDGET_STATE } from '../../../model/enums';
import { WizNgxCharts } from '../../../components/wiz-ngx-charts/wiz-ngx-charts.component';
import { ChartConfigObj } from '../../../components/wiz-ngx-charts/config/chart-config.obj';
import { ChartItemObj } from '../../../components/wiz-ngx-charts/config/chart-item.obj';
import { ChartSerieObj } from '../../../components/wiz-ngx-charts/config/chart-serie.obj';
import { ChartSettingsPop } from './pop/chart-settings.pop';
import { WidgetBasic } from '../base/widget-basic';
import { ChronocodeVO } from '../../../vo/core/chronocode.vo';
import { StyleVO } from '../../../vo/workingdata/style.vo';
import { FreedataValueVO } from  '../../../vo/core/freedatavalue.vo';
import { IWidget, IWidgetFilter } from '../../../manager/widgets/i-widget';
import { AlkEvent } from '../../../framework/events/alk.event';
import { ChartBuilderItem } from '../../../manager/charts/charts.engine';
import { ViewData } from '../../../model/view.data';

@Component({
  selector: 'chart-widget',
  templateUrl: './chart.widget.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** un chart */
export class ChartWidget extends WidgetBasic {
  @HostBinding('style.padding') pad: string = '5px';
  
  constructor(elementRef: ElementRef) {
    super(elementRef);
    this.widgetKind = WIDGET_KIND.CHART;
    this.widgetGlobalKind = WIDGET_GLOBAL_KIND.BASELINE;
    this.settingsPopClassFactory = ChartSettingsPop;
  }

  /* ##############################################################################
                  composants spécifiques
  ############################################################################## */

  @ViewChild('chart') chart: WizNgxCharts; // le chart
  @ViewChild('ti') ti: WizTextInput; // le chart

  /* ##############################################################################
                  paramètres spécifiques
  ############################################################################## */

  @Input() public mode: CHART_WIDGET_MODE = null; // mode de restitution du chart

  /* ##############################################################################
                    alimentation des données FEI / construction du grid
  ############################################################################### */

  ngAfterViewInit() {
    super.ngAfterViewInit();
    // save d'un formulaire de FD. Possible mise à jour visuelle des Key / filters, e.data = DataPack
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.FREEDATA_UPDATED, this.refreshOnFreedataUpdate);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.FREEDATA_UPDATED);
  }

  /** synchro des valeurs de FDVs sur retour de form Freedata  */
  private refreshOnFreedataUpdate(e: AlkEvent): void {
    // console.log('fdv update on charts', e);
    const updates: Map<string, FreedataUpdatePack> = e.data; 


    let fdCodeSet: Set<string> = new Set(updates?.keys());
    let isIn: boolean = false;
    try {
      this.context?.c?.forEach(wcio => {
        if(fdCodeSet.has(wcio.cod.split(WizConstants.CONTEXT_SEPARATOR)[0])) {
          isIn = true;
        }
        // if(wcio.cod.split(WizConstants.CONTEXT_SEPARATOR)[0] === fdCode) {
        //   isIn = true;
        // }
      });
      this.context?.r?.forEach(wcio => {
        if(fdCodeSet.has(wcio.cod.split(WizConstants.CONTEXT_SEPARATOR)[0])) {
          isIn = true;
        }
      });
      this.context?.d?.forEach(wcio => {
        if(fdCodeSet.has(wcio.cod.split(WizConstants.CONTEXT_SEPARATOR)[0])) {
          isIn = true;
        }
      });
    } catch(e: any) {

    }

    if(isIn) {
      // console.log('isIn');
      // TODO TODO seulement si FD utilisé pour le charts
      this.drawOrRefresh(WIDGET_ACTION_ORIGIN.GLOBAL_FILTERING);
    }

  }


  /**
   * actions jouées sur le drawContent, le alterContent, ou en cas de modifs de filtre, de sélection
   * @override
   * @param origin WIDGET_ACTION_ORIGIN pour cibler qui demande
   * @param data l'objet éventuellement porté
   */
  public drawOrRefresh(origin: WIDGET_ACTION_ORIGIN, data?: any): void {
    
    let mustBuildRows: boolean = false; // on doit construire les lignes
    let mustRefresh: boolean = false; // création mise à jour de la DS de la liste
    // TODO TODO pas toujours MustRefresh

    // première création
    if(origin === WIDGET_ACTION_ORIGIN.INIT) {
      // mustBuildRows = true;
      // mustRefresh = true;
    }
    
    // première création
    if(origin === WIDGET_ACTION_ORIGIN.READY) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    // fin de calcul d'un FEI requis pour mes calculs
    if(origin === WIDGET_ACTION_ORIGIN.REQUIRED_COMPUTED) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    // modification du scenario global ...
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_SCENARIO_UPDATE) {
      mustBuildRows = true;
      mustRefresh = true;
    }

    // TODO sur configuration, capter les delta filter, colonnes, fields...

    // TODO TODO détecter une modification structurelle en cas 
    // contextDamaged
    // origine: changement de config
    // et mode pivot, sinon les lignes sont toujours OK
    if(origin === WIDGET_ACTION_ORIGIN.CONFIGURE) {   
      mustBuildRows = true;
      mustRefresh = true;
    } 

    // modification du filtre global ... on filtre, et on corrige éventuellement la sélection
    if(origin === WIDGET_ACTION_ORIGIN.GLOBAL_FILTERING) {
      mustRefresh = true;
    }


    if(origin === WIDGET_ACTION_ORIGIN.OUTER_SELECT) {
      // console.log('outer select', this.widgetKind, this.fei.uniqueName, this.state);
      mustBuildRows = true;
      mustRefresh = true;
    }

    if(origin === WIDGET_ACTION_ORIGIN.RECOMPUTE) {
      let loopResults: Map<FeiVO, LoopResult> = data[0];  
      if(this.fei.scenario === WizConstants.SCEN_KEY_CODE) {
        [...loopResults.keys()].forEach((fei: FeiVO) => {
          if(fei.visionStepLevel === this.fei.visionStepLevel) {
            if(loopResults.get(fei).created.size > 0 || loopResults.get(fei).deleted.size > 0) {
              mustBuildRows = true;
              mustRefresh = true;
            } else {
              mustRefresh = true;
            }
          }
        });
      }
      
      // gestion multiscen... prendre tous les loopers du même niveau
      let loop: LoopResult = loopResults.get(this.fei);
      if(loop?.created.size > 0 || loop?.deleted.size > 0) {
        mustBuildRows = true;
        mustRefresh = true;
      } else {
        mustRefresh = true;
      }
    }

    if(mustBuildRows) {
      this.buildLines(); // délégué au WidgetBasic
    }

    if(mustRefresh) {
      this.mainDS.refresh();
      this.buildChart();
    }
  }

  /**
   * construction 'initiale' des objets graphiques du widget :
   * - sur init véritable
   * - sur modification de la datasource
   * - sur modification de scenario (modifie la DS)
   */
  public drawContent(): void {
    
    let master: IWidget = this.model.widgetEngine.checkFirstMaster(this); // je cherche un master. S'il y en a un, READY n'est pas suffisant
    // si j'ai un master et qu'il n'est pas prêt, on ne fait rien
    if (!!master && master.state !== WIDGET_STATE.SELECTION_COMPLETE) { // j'ai un master, mais il n'est pas prêt
      // console.log('checkFeiState master not ready', this.widgetKind, this.fei.uniqueName);
      return;
    }

    if(this.state >= WIDGET_STATE.READY) { // déjà construit, plus rien a faire
      return;
    }

    this.drawOrRefresh(WIDGET_ACTION_ORIGIN.INIT); // actions communes draw / alter
    super.drawContent(); // pas au début, c'est lui qui dispatch les events
  }

  /**
   * reconstruction des objets graphiques du widget sur changement de structure : 
   * - modification des filtres qui impactent les colonnes
   * - changement de champs en ligne / colonnes / data
   * - changement de filtre
   * - changement de sélection
   * @param origin 
   */
  public alterContent(origin: WIDGET_ACTION_ORIGIN): void {
    this.drawOrRefresh(origin);
    super.alterContent(origin);
  }

  /**
   * construction du chart
   * this.context.R => les éléments qui constituent les libellés (et le regroupement)
   * this.context.D => les mesures simples
   * this.context.C => les mesures pour l'axe X (linéaire)
   */
  private buildChart(): void {
    // WizPopUp.toast('buildChart', WizIcon.TOAST_SUCCESS).show();
    if(this.context?.r?.length < 1 || this.context?.d?.length < 1) { // pas assez
      return;
    }

    let chartSeries: ChartSerieObj[] = []; // les séries

    if ( this.context?.c?.length === 1 
          && this.context.ct !== CHART_WIDGET_MODE.MULTI_LINE) { // une seule mesure avec axe X temporel 
      // lines ou bubbles
      // TODO interception Bubble, 2 mesures...
      // WizPopUp.toast('buildLinear', WizIcon.TOAST_SUCCESS).show();
      chartSeries = this.buildLinear();
    } 
    else if (this.context.ct === CHART_WIDGET_MODE.MULTI_LINE) { // une ou plusieurs mesures avec axe X temporel 
      // multilines
     //  WizPopUp.toast('buildMultiDimensions', WizIcon.TOAST_SUCCESS).show();
      chartSeries = this.buildMultiDimensions();
    } else if ( this.context.d.length > 1 
                && this.context.ct !== CHART_WIDGET_MODE.LINE) { // multi-mesures sans axe X
      // BarStacked
      // WizPopUp.toast('buildMultiMeasures', WizIcon.TOAST_SUCCESS).show();
      chartSeries = this.buildMultiMeasures();
    } else { // une mesure, pas d'axe X
      // tout le reste
      // WizPopUp.toast('buildSingleMeasure', WizIcon.TOAST_SUCCESS).show();
      chartSeries = this.buildSingleMeasure();
      
    }

    const chartConfig: ChartConfigObj = new ChartConfigObj(
      this.context.ct as CHART_WIDGET_MODE,
      chartSeries,
      this.context.cp
    );
    let real: WizNgxCharts = new WizNgxCharts(null, null, null);
    if(!!this.chart) { // instancié ?
      this.chart.config = chartConfig;
    }
    
      
  }

  /**
   * graph linéaire, une seule mesure
   * @returns 
   */
  private buildLinear(): ChartSerieObj[] { 
    
    let chartSeries: ChartSerieObj[] = [];

    let serie: ChartSerieObj; // une série de valeurs à alimenter
    let items: ChartItemObj[]; // les éléments de la série

    
    const xAxisPKD: FeiKeyDef = BaselineEngine.getKey(this.fei, this.context.c[0].cod); // le champ qui sert pour l'axe X
    const yAxisPKS: FeiKeyDef = BaselineEngine.getKey(this.fei, this.context.d[0].cod); // le champ qui sert pour l'axe y
    const yAxisAggFunc: string = this.context.d[0].agg; // méthode d'aggrégat pour les valeurs de l'axe y

    // collecte des # valeurs pour l'axe X : soit une valeur Key/filter ou une mesure
    // on ventile dans une map pour garder l'unicité : valeur (string) = [label, rawVal (pour sort)]
    const xValsMap: Map<string, KeyPair> = new Map<string, KeyPair>();

    let itemLabel: string[]; // le libellé de l'item (la série), concaténation des libellés des champs row (OBM-Mars, OBM-Avril...)
    let rKey: KeyPair; // les trios de valeurs
    let baselinesByX: Map<string, WidgetLine[]>; // ventilation des baselines de la pl par valeur de l'axe x pour création des items

    this.mainDS.getWorkedSource()?.forEach((pl: WidgetLine) => { // pour chaque ligne, prise de la valeur (aggrégée) du champ
   
      itemLabel = []; // gestion des séries et de leur libellé (clé de série = pl.uniqueKey)
      items = [];
      baselinesByX = new Map<string, WidgetLine[]>();

      this.context.r.forEach((wcoi: WidgetConfigurationItemObj) => {
        itemLabel.push(BaselineEngine.getLabel(this.fei, pl, wcoi.cod, wcoi.fdvf));
      });

      serie = new ChartSerieObj(pl.key, itemLabel.join(' - '));
      chartSeries.push(serie); // création de la série, le label est la value Row); // et ajout à la collection

      // collecte des # valeurs pour l'axe x pour cette pl (selon les valeurs des ses Baselines)
      [...pl.ch.values()].forEach((plAgg: WidgetLine) => {
        
        // appliquer le filtre global si existe...
        let isGood: boolean = this.mainFilterFunction(plAgg);
        // console.log('subFilter on charts', plAgg.key, isGood);

        if(isGood) {
          rKey = null;
          if (xAxisPKD.isMeasure) {
            // l'axe x est une mesure
            const rawVal: any = BaselineEngine.getValue(this.fei, plAgg, plAgg.cells.get(this.context.c[0].cod));
            if (!!rawVal) {
              const label: string = this.model.formatEngine.applyFormat(rawVal, xAxisPKD.datakind, this.context.c[0].fmt, null);
              const sortVal: string = rawVal;
              rKey = {r: rawVal, l: label, s: sortVal};
            }
          } else {
            // vision ou filter
            let cell: AkmFDVCell = BaselineEngine.checkFDVCell(this.fei, plAgg.bl, this.context.c[0].cod, WizConstants.FIELD_LABEL);
            rKey = {r: cell.rawVal, l: cell.label, s: cell.sortKey};
          }
  
          if (rKey) {
            // pas forcément de valeurs d'axe x pour la pl / pour les lignes
            xValsMap.set(rKey.r, rKey); // mémorisation des # valeurs de x pour restituer les labels et le sort
            if (!baselinesByX.has(rKey.r)) {
              baselinesByX.set(rKey.r, []);
            }
            baselinesByX.get(rKey.r).push(plAgg);
          }
        }

      });

      // puis on calcule chaque pool pour en faire les items
      let childPool: WidgetLine[];
      let value: any;

      Array.from(baselinesByX.keys()).forEach((rawVal: string) => {
        childPool = baselinesByX.get(rawVal);
        
        value = this.model.aggregateEngine.call(yAxisAggFunc, this.fei, childPool, yAxisPKS); // calcul de la valeur Y
        // console.log('item', itemLabel.join(' - '), xValsMap.get(rawVal).label, rawVal, value);
        items.push(
          new ChartItemObj(
            rawVal,
            xValsMap.get(rawVal).l,
            Number(value),
            this.model.formatEngine.applyFormat(value, yAxisPKS.datakind, this.context.d[0].fmt, this.context.d[0].agg),
            xValsMap.get(rawVal).s
          )
        );
      });

      serie.items = items.sort((a, b) => a.sortKey - b.sortKey);
    });
    // console.log('buildLinear', chartSeries);
    return chartSeries;
  }

  /**
   * construction des séries simple, une seule mesure, pas d'axe X
   * (NumberCard, Treemap, gauges...)
   * ... mais aussi les stacked qui n'ont qu'une mesure...
   * @returns 
   */
  private buildSingleMeasure(): ChartSerieObj[] {
    let chartSeries: ChartSerieObj[] = [];
    let serie: ChartSerieObj; // une série de valeurs à alimenter
    let items: ChartItemObj[]; // les éléments de la série

    // couleur custom sur le 1er field en ligne
    let customColorField: WidgetConfigurationItemObj = this.context.f?.length > 0 ? this.context.f[0] : null;
    // console.log('customColorField', customColorField);


    // une seule mesure, ajout des items directement dans la 1ère série
    this.context.d.forEach((measureWcio: WidgetConfigurationItemObj) => {
      serie = new ChartSerieObj(measureWcio.cod, measureWcio.cod); // création de la série
      chartSeries.push(serie); // et ajout à la collection
      items = [];

      // style conditionnel sur la première mesure
      let conditionalColorField: string = measureWcio.sty;
      let style: StyleVO = null;
      if(conditionalColorField) {
        const parts: string[] = conditionalColorField.split(WizConstants.KEY_SEPARATOR); // nommage des style vision_code#code
        style = this.model.styleEngine.getStyle(parts[0], parts[1]);
      }

      const fkd: FeiKeyDef = BaselineEngine.getKey(this.fei, measureWcio.cod);
      if(fkd) {
        this.mainDS.getWorkedSource()?.forEach((wl: WidgetLine) => { // pour chaque ligne
          const itemLabel: string[] = [];
          let lbl: string;
          this.context.r.forEach((wcoi: WidgetConfigurationItemObj) => {
            let fkd: FeiKeyDef = this.fei.keyMap.get(wcoi.cod);
            if(fkd.isMeasure) {
              // console.log('chart lbl', lbl, wcoi.cod, fkd.measure);
              lbl = BaselineEngine.getRawKeyOrValue(this.fei, wl, wcoi.cod);
              // interception FILTER 
              if(fkd.datakind === FIELD_KIND.FILTER) { 
                lbl = this.filterRender(fkd, lbl);
              } 
              // override mode liste... Descendre ça dans getRawKeyOrValue
            } else {
              lbl = BaselineEngine.getLabel(this.fei, wl, wcoi.cod, wcoi.fdvf || WizConstants.FIELD_LABEL);
            }
            
            itemLabel.push(lbl);
          });
          
          let itemColor: string = null;
          if(customColorField && this.context.r.length > 0) {
            itemColor = BaselineEngine.getLabel(this.fei, wl, this.context.r[0].cod, customColorField.cod) || null;
            // console.log('col', wl.key, itemColor);
          }

          const realValue: string = this.customValueAccessor(wl, measureWcio, fkd);
          const formattedValue = this.model.formatEngine.applyFormat(realValue, fkd.datakind, measureWcio.fmt, measureWcio.agg);
          const sortKey = BaselineEngine.getSort(this.fei, wl, this.context.r[0].cod, measureWcio.fdvf || WizConstants.FIELD_LABEL);
  
          let cio: ChartItemObj = new ChartItemObj(wl.key, itemLabel.join(' - '), Number(realValue), formattedValue, sortKey);
          
          cio.color = itemColor;

          // gestion du style conditionnel
          
          if(!!style) {
            // on recycle les params des pivots
            let fakeWL: WidgetLine = cloneDeep(wl);

            let styleResult: any = this.model.widgetEngine.chartComputeStyle(this.fei, style, wl, realValue);
            if(styleResult) {
              cio.color = styleResult.backgroundColor;
            }
            // console.log('cond style', styleResult, realValue);
          }    
          
          
          items.push(cio);
        });
  
        const sortPKD: FeiKeyDef = BaselineEngine.getKey(this.fei, this.context.r[0].cod);
        const sortField: WDSSortField = this.model.dataEngine.pkdSortField(sortPKD);
        serie.items = WizCollectionUtils.doSort(items, [sortField]);
      }

    });

    // console.log('buildSingleMeasure', chartSeries);
    return chartSeries;
  }

  /**
     * construction des séries multi-mesures, pas d'axe X
     * (barStacked)
     * @returns 
     */
  private buildMultiMeasures(): ChartSerieObj[] {
    let chartSeries: ChartSerieObj[] = [];
    let serie: ChartSerieObj; // une série de valeurs à alimenter
    let items: ChartItemObj[]; // les éléments de la série
  
    this.mainDS.getWorkedSource()?.forEach((wl: WidgetLine) => {
           
      // pour chaque ligne
      const itemLabel: string[] = [];
      let lbl: string;
      serie = new ChartSerieObj(wl.key, null);
      this.context.r.forEach((wcoi: WidgetConfigurationItemObj) => {
        
        let fkd: FeiKeyDef = this.fei.keyMap.get(wcoi.cod);
        if(fkd.isMeasure) {
          // console.log('chart lbl', lbl, wcoi.cod, fkd.measure);
          lbl = BaselineEngine.getRawKeyOrValue(this.fei, wl, wcoi.cod);
          // interception FILTER 
          if(fkd.datakind === FIELD_KIND.FILTER) { 
            lbl = this.filterRender(fkd, lbl);
          } 
          // override mode liste... Descendre ça dans getRawKeyOrValue
        } else {
          lbl = BaselineEngine.getLabel(this.fei, wl, wcoi.cod, wcoi.fdvf || WizConstants.FIELD_LABEL);
        }
        itemLabel.push(lbl);
        
        serie['SORT'+ wcoi.cod] = BaselineEngine.getSort(this.fei, wl, wcoi.cod ,WizConstants.FIELD_LABEL);
        // TODO TODO bourrer avec des 0 pour les numbers...
        // itemkeys.push(BaselineEngine.getSort(this.fei, wl, wcoi.cod ,WizConstants.FIELD_LABEL));
      });
      serie.label = itemLabel.join(' - '); // création de la série

      chartSeries.push(serie); // et ajout à la collection
      items = []; // les items de la série
      this.context.d.forEach((measureWcio: WidgetConfigurationItemObj) => { // alimentation de chaque mesure
        const fkd: FeiKeyDef = BaselineEngine.getKey(this.fei, measureWcio.cod);
        if(fkd) {
          const realValue: string = this.customValueAccessor(wl, measureWcio, fkd);
          const formattedValue = this.model.formatEngine.applyFormat(realValue, fkd.datakind, measureWcio.fmt, measureWcio.agg);
          items.push(new ChartItemObj(measureWcio.cod, measureWcio.lbl, Number(realValue), formattedValue));
        }
      });

      serie.items = items;
    });

    const sortFields: WDSSortField[] = [];
    this.context.r.forEach((wcoi: WidgetConfigurationItemObj) => {
      const fkd: FeiKeyDef = BaselineEngine.getKey(this.fei, wcoi.cod);
      sortFields.push({ property: 'SORT'+ wcoi.cod, numeric: this.model.dataEngine.datakindIsNumeric(fkd.datakind) , way: 'asc' });
    });

    chartSeries = WizCollectionUtils.doSort(chartSeries, sortFields);

    return chartSeries;
  }

  /**
   * chart avec axes X et Y
   * axe X linéaire (context.C)
   * axe Y plusieurs mesures (context.D)
   * @returns chartSeries
   */
  // private buildMultiDimensionsOld(): ChartSerieObj[] {
  //   let chartSeries: ChartSerieObj[] = [];
  //   const rowWcio: WidgetConfigurationItemObj = this._context.r[0]; // prise de la conf des lignes
    
  //   let pool: WidgetLine[] = [...this.mainDS.getWorkedSource()];

  //   let cell: AkmFDVCell; // la référence
  //   let serie: ChartSerieObj; // une série de valeurs à alimenter
  //   let items: ChartItemObj[]; // les éléments de la série
  //   let xAxisFKD: FeiKeyDef; // la définition de mesure pour l'axe X
  //   let xAxisMap: Map<FeiKeyDef, KeyPair[]> = this.buildXAxisMap(); // construction des clés pour l'axe X

  //   // axes corrigés, suppression des éléments totalement nulls
  //   let xAxisMapNeo: Map<FeiKeyDef, KeyPair[]> = new Map(); // construction des clés pour l'axe X


  //   this.context.d.forEach((measureWcio: WidgetConfigurationItemObj) => { // pour chaque mesure
  //     const measureFKD: FeiKeyDef = BaselineEngine.getKey(this.fei, measureWcio.cod);
  //     if(measureFKD) {
  //       // on balaie les WidgetLines éligibles
        


  //       // pour chaque ligne éligible
  //       pool.forEach((wl: WidgetLine) => { 

  //         // prise de la cellule FDV pour obtenir la valeur de l'axe ?
  //         cell = BaselineEngine.checkFDVCell(this.fei, wl.bl, rowWcio.cod, WizConstants.FIELD_LABEL);
          
  //         if(!!cell) {
  //           // création de la série (code, label)
  //           serie = new ChartSerieObj(`${cell.rawVal}|${measureWcio.cod}`, `${cell.label} - ${measureWcio.lbl}`);
  //           chartSeries.push(serie); // et ajout à la collection
            
  //           // préparation des items pour cette série
  //           items = []; 

  //           this.context.c.forEach((xAxisWcio: WidgetConfigurationItemObj) => { // pour chaque configuration de l'axe X
  //             xAxisFKD = BaselineEngine.getKey(this.fei, xAxisWcio.cod); // l'élément qui sert d'axe X
              
  //             xAxisMap.get(xAxisFKD).forEach((keypair: KeyPair) => {
  //               // pour chaque ligne
  //               const realValue: string = this.customValueAccessor(wl, measureWcio, measureFKD, { trioKey: xAxisFKD.fullCode, trioRawValue: keypair.r });
  //               if(realValue !== null) {
  //                 const formattedValue = this.model.formatEngine.applyFormat(realValue, measureFKD.datakind, measureWcio.fmt, measureWcio.agg);
  //                 items.push(new ChartItemObj(keypair.r, keypair.l, Number(realValue), formattedValue, keypair.s));
  //               }

  //             });
              
  //             // on tri les items
  //             const sortPKD: FeiKeyDef = BaselineEngine.getKey(this.fei, this.context.r[0].cod);
  //             const sortField: WDSSortField = this.model.dataEngine.pkdSortField(sortPKD);
  //             serie.items = WizCollectionUtils.doSort(items, [sortField]);
  //           });
  //         }
          
  //       });
  //     }

  //   });
    
  //   // console.log('buildMultiDimensions', chartSeries);
  //   return chartSeries;
  // }


  /**
   * méthode d'accès générique sur les baselineValues.valor, depuis la WidgetLine
   * @param wl la conf d'une cellule de grid. .data contient une WidgetLine
   * @param dataCol le context.data à calculer
   * @param fkd 
   * @param filterPool 
   * @returns 
   */
  public customValueAccessor(wl: WidgetLine, dataCol: WidgetConfigurationItemObj, fkd: FeiKeyDef, filterPool?: { trioKey: string, trioRawValue: any }): string {
    let result: string = null;
    const wcio: WidgetConfigurationItemObj = dataCol;
    let childPool: WidgetLine[] = [...wl.ch.values()];

    // on filtre, y'a peut-être un filtre global
    if(childPool.length > 0) {
      childPool = childPool.filter(item => this.mainFilterFunction(item));
    }

    if(fkd) {
      if (!!filterPool) {
        childPool = childPool.filter(pline => {
          return BaselineEngine.getRawKey(this.fei, pline, filterPool.trioKey) === filterPool.trioRawValue;
        });
      }
      if(childPool?.length > 0) {
        result = this.model.aggregateEngine.call(wcio.agg, this.fei, childPool, fkd);
      }
    }

    return result;
  }

  /**
   * application du filtre global sur les éléments de l'axe des X
   * @param xAxisFKD feiKeyDef de l'axe
   * @param coll collection de FDV ou Chronos
   * @returns la collection filtrée
   */
  private axisGlobalFilter(xAxisFKD: FeiKeyDef, coll: any[]): any[] {
    let result: any[] = [...coll];
    
    
    if(!!this.model.widgetEngine.globalFilter) { 
      let globalFilter: IWidget = this.model.widgetEngine.checkGlobalFilter();
      
      let filter: IWidgetFilter; // les filtres qu'on va jouer
      if(globalFilter.filters) { // filtre actifs sur le global
        
        let bestFilterMap: Map<string, IWidgetFilter> = new Map();
        for (var i = 0, len = globalFilter.filters.size; i < len; i++) {
          filter = [...globalFilter.filters.values()][i];
          if(filter.active) { // rien à foutre des inactifs
            if(!bestFilterMap.has(filter.fkd.code)) { // pas dans la map, on prend
              bestFilterMap.set(filter.fkd.code, filter);
            } else { // dans la map, on prend si plus précis
              if(filter.fkd.level > bestFilterMap.get(filter.fkd.code).fkd.level) {
                bestFilterMap.set(filter.fkd.code, filter);
              }
            }
          }
        }


        if(bestFilterMap.has(xAxisFKD.code)) { // c'est un filtre sur mon périmètre
          filter = bestFilterMap.get(xAxisFKD.code);
          // console.log('glob filter', xAxisFKD.code, filter);

          // 3 cas possibles: 
          let delta: number = xAxisFKD.level - filter.fkd.level;
          // le filtre est plus précis que moi
          // console.log('delta', xAxisFKD.code, filter.values);

          // TODO TODO jouer les comparators sur le filtre pour obtenir les vraies valeurs

          let filteredResult: any[] = [];
          result.forEach(item => {

            if(delta === 0) { // filtre de même niveau
              if(filter.values.has(item.code)) {
                filteredResult.push(item);
              }
            } else if(delta > 0) { // le filtre est sur un parent
              let realKeyParts: string[] = item.code.split(WizConstants.CODE_LEVEL_SEPARATOR);
              let realKey: string = realKeyParts.slice(0, xAxisFKD.level - delta).join(WizConstants.CODE_LEVEL_SEPARATOR);
              // console.log('realKey', realKey);
              if(filter.values.has(realKey)) {
                filteredResult.push(item);
              }
            }
            else if(delta < 0) { // le filtre est sur un enfant, pas concerné
              filteredResult.push(item);
            }
          });

          result = [...filteredResult];
        }      
      }
    } 

    return result;
        
  }


  /**
   * chart avec axes X et Y
   * axe X linéaire (context.C)
   * axe Y plusieurs mesures (context.D)
   * @returns chartSeries
   */
  private buildMultiDimensions(): ChartSerieObj[] {
    // ce qu'on va construire
    let chartSeries: ChartSerieObj[] = [];
    

    let cell: AkmFDVCell; // la référence

    let serieCode: string;
    let serieLabel: string;

    let serie: ChartSerieObj; // une série de valeurs à alimenter
    let items: ChartItemObj[]; // les éléments de la série
    let item: ChartItemObj; // un élément
    let itemValue: string = null; // la valeur pour un couple ligne-mesure pour une position sur l'axe X
    let formattedValue: string = null; // la valeur formatée


    // champ utilisé pour différencier les lignes (clé)
    const lineKey: string = this._context?.r?.length > 0 ? this._context.r[0].cod : null; 
    if(!lineKey) {
      return chartSeries;
    }

    let xAxis: ChartBuilderItem[] = this.buildXAxisMap(); // construction des clés pour l'axe X

    // prise du pool éligible et des mesures cibles
    let lines: WidgetLine[] = [...this.mainDS.getWorkedSource()];
    // prise des mesures et de leur conf
    let measures: Map<WidgetConfigurationItemObj, FeiKeyDef> = new Map();
    let measFKD: FeiKeyDef;


    this.context.d.forEach((measureWcio: WidgetConfigurationItemObj) => { // pour chaque mesure
      const meas: FeiKeyDef = BaselineEngine.getKey(this.fei, measureWcio.cod);
      if(meas) {
        measures.set(measureWcio, meas);
      }
    });

    // on construit un cartésien Line * mesures
    lines.forEach((wl: WidgetLine) => {
      [...measures.keys()].forEach((mWcio: WidgetConfigurationItemObj) => {

        
        // prise de la cellule
        cell = BaselineEngine.checkFDVCell(this.fei, wl.bl, lineKey, WizConstants.FIELD_LABEL);  
        if(!!cell) {
          // ça donne une série
          serieCode = [cell.rawVal, mWcio.cod].join(ViewData.KEY_SEPARATOR);
          serieLabel = [cell.label, mWcio.lbl].join(' - ');
          serie = new ChartSerieObj(serieCode, serieLabel);
          chartSeries.push(serie);

          // sur chaque item
          xAxis.forEach((cbi: ChartBuilderItem) => {

            measFKD = measures.get(mWcio);
            itemValue = this.customValueAccessor(wl, mWcio, measFKD, { trioKey: cbi.masterKey, trioRawValue: cbi.key });
            if(itemValue !== null) {
              cbi.active = true;
              formattedValue = this.model.formatEngine.applyFormat(itemValue, measFKD.datakind, mWcio.fmt, mWcio.agg);
            }
            item = new ChartItemObj(cbi.key, cbi.label, Number(itemValue), formattedValue, cbi.sortKey);
            cbi.itemMap.set(serieCode, item);      
          });
        }
      });
    });

    // maintenant, on supprime les extrêmes vides
    let xSurvivors: ChartBuilderItem[] = [...xAxis];
    let realIdx: number = 0;
    let hit: boolean = false;
    xSurvivors.forEach((cbi: ChartBuilderItem) => {
      if(!cbi.active && !hit) {
        realIdx++;
      } else {
        hit = true;
      }
    });
    xSurvivors = xSurvivors.slice(realIdx, xSurvivors.length);
    xSurvivors = xSurvivors.reverse(); // on inverse pour enlever la fin

    realIdx = 0;
    hit = false;
    xSurvivors.forEach((cbi: ChartBuilderItem) => {
      if(!cbi.active && !hit) {
        realIdx++;
      } else {
        hit = true;
      }
    });

    xSurvivors = xSurvivors.slice(realIdx, xSurvivors.length);
    xSurvivors = xSurvivors.reverse(); //on remet dans l'ordre

    // on alimente les séries
    chartSeries.forEach((cso: ChartSerieObj) => {
      cso.items = [];
      xSurvivors.forEach((cbi: ChartBuilderItem) => {
        cso.items.push(cbi.itemMap.get(cso.name));
      });
    });


    // console.log('buildMultiDimensions', chartSeries);
    return chartSeries;
  }

  /**

   * construction de la map des valeurs pour l'axe X
   * @returns ChartBuilderItem, les clés pour chaque élément de l'axe
   */
  private buildXAxisMap(): ChartBuilderItem[] {
    
    // les clés à générer
    let xAxis: ChartBuilderItem[] = [];

    let xAxisWcio: WidgetConfigurationItemObj = this.context?.c?.length > 0 ? this.context.c[0] : null;
    if(!xAxisWcio) {
      return xAxis;
    }
    let xAxisFKD: FeiKeyDef = BaselineEngine.getKey(this.fei, xAxisWcio.cod); // la définition de mesure pour l'axe X
    if(!xAxisFKD) {
      return xAxis;
    }

    let fdvValues: FreedataValueVO[];
    let chronoValues: ChronocodeVO[];

    if(xAxisFKD.isChrono) {
      const vsk: VisionKey = xAxisFKD.visionKey;
      if (!!vsk) {
        let mainChrono: ChronocodeVO = this.model.chronocodeEngine.getChronocode(vsk.chronoCode)
        chronoValues = this.model.chronocodeEngine.getChildrenByLevel(mainChrono, xAxisFKD.level);
        chronoValues = this.axisGlobalFilter(xAxisFKD, chronoValues);
        chronoValues?.forEach((chrono: ChronocodeVO) => {
          // TODO ici, prise du champ sur le chrono
          let lbl: any = chrono.trsLabel;
          const fdvfield: string = xAxisWcio.fdvf; 
          if(!!fdvfield) { // formatage complémentaire pour les rendus FDVs avec propriété (champ custom)
            lbl = chrono[fdvfield];
            let finalDatakind = this.model.chronocodeEngine.getChronoFieldByCode(fdvfield).datakind;
            lbl = this.model.formatEngine.applyFormat(chrono[fdvfield]?.toString() || null, finalDatakind, xAxisWcio.fmt, null); // et formater
          }



          xAxis.push({active: false, itemMap: new Map(),
                      masterKey: xAxisFKD.fullCode,
                      key: chrono.code, 
                      sortKey: chrono.startDate,
                      label: lbl});
        });
      }
    } else if (xAxisFKD.isFreedata) {
      fdvValues = this.model.freedataEngine.getFreedataValuesByLevel(xAxisFKD.fullCode);
      fdvValues = this.axisGlobalFilter(xAxisFKD, fdvValues);
      fdvValues?.forEach((fdv: FreedataValueVO) => {
        xAxis.push({active: false, itemMap: new Map(),
                    masterKey: xAxisFKD.fullCode,
                    key: fdv.code, 
                    sortKey: fdv.trsLabel,
                    label: fdv.trsLabel});
      });
    }

    // on peut trier tout de suite sur sortKey
    const sortField: WDSSortField = this.model.dataEngine.pkdSortField(xAxisFKD);
    xAxis = WizCollectionUtils.doSort(xAxis, [sortField]);

    return xAxis;
  }

  /** demande d'export depuis le bouton */
  public exportDatas(kind?: string): void {
    if(!kind || kind === 'excel') {
      // pas d'export Excel pour lui this.model.excelExportEngine.exportAsExcel('allkeyme', this);
    } else {
      this.model.excelExportEngine.exportAsPdf('allkeyme', this, this.elementRef, null, null);
    }
  }

  private filterRender(fkd: FeiKeyDef, rawValue: string): string 
  {
    let result: string = rawValue;

    try {
      let FDCode: string = (fkd.measure.settings as any).keys[0].FD;
      let FDVCode: string = rawValue;
      if(FDCode && FDVCode) {
        let renderField: string = (fkd.measure.settings as any).keys[0].render || ViewData.FIELD_LABEL;
        let fdv: FreedataValueVO = this.model.freedataEngine.getFreedataValue(FDCode, FDVCode);
        if(!!fdv) {
          result = this.model.freedataEngine.getFDValue(fdv, renderField);
        }
      }
    } catch (e:any) {

    }
    return result;
  }

}
